
import { Card, CardHeader } from "@/components/ui/card";
import { useProductSubscriptions } from "@/hooks/useProductSubscriptions";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ProductActions } from "./product/ProductActions";
import { VisibilityToggle } from "./product/VisibilityToggle";
import { ProductContent } from "./product/ProductContent";

interface ProductCardProps {
  id: string;
  title: string;
  description: string;
  features: string;
  link: string;
  logo: string;
  product_screen: string;
  visible: boolean;
  onDelete: (id: string) => void;
  onUpdate: () => void;
}

export const ProductCard = ({ 
  id, 
  title, 
  description,
  features, 
  link,
  logo,
  product_screen,
  visible = true,
  onDelete,
  onUpdate 
}: ProductCardProps) => {
  const { hasSubscriptions } = useProductSubscriptions(id);
  const [isVisible, setIsVisible] = useState(visible);
  const { toast } = useToast();

  const toggleVisibility = async () => {
    try {
      const newVisibility = !isVisible;
      
      const { error } = await supabase
        .from('products')
        .update({ visible: newVisibility })
        .eq('id', id);
      
      if (error) throw error;
      
      setIsVisible(newVisibility);
      toast({
        title: newVisibility ? "Product Visible" : "Product Hidden",
        description: newVisibility 
          ? "This product will now be shown on the home page." 
          : "This product will be hidden from the home page.",
      });
    } catch (error) {
      console.error('Error toggling product visibility:', error);
      toast({
        title: "Error",
        description: "Failed to update product visibility",
        variant: "destructive",
      });
    }
  };

  return (
    <Card className="hover:shadow-lg transition-shadow relative">
      <CardHeader className="pb-0">
        <div className="flex items-center justify-between">
          <VisibilityToggle 
            id={id}
            isVisible={isVisible}
            onToggle={toggleVisibility}
          />
          <ProductActions
            id={id}
            title={title}
            description={description}
            features={features}
            link={link}
            logo={logo}
            product_screen={product_screen}
            isVisible={isVisible}
            hasSubscriptions={hasSubscriptions}
            onDelete={onDelete}
            onUpdate={onUpdate}
          />
        </div>
      </CardHeader>
      <ProductContent 
        title={title}
        description={description}
        logo={logo}
      />
    </Card>
  );
};
